// Couche HTTP partagée par les trois clients de provisioning (Coolify, GitHub,
// Cloudflare).
//
// Elle existe pour une raison : `--dry-run`. Le provisioning crée de l'infra
// facturée par des appels NON idempotents (un second run crée un second projet
// Coolify, un second repo…). On veut donc pouvoir imprimer le plan d'appels sans
// en émettre un seul, et ça ne marche que si tout passe par un point unique.

export type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE"

export interface HttpCall {
	method: HttpMethod
	url: string
	body?: unknown
	/** Libellé lisible, imprimé en tête de l'appel en dry-run. */
	label: string
}

export interface RequestOptions extends HttpCall {
	headers?: Record<string, string>
	/**
	 * Valeur rendue en dry-run à la place de la réponse réelle. Sans elle, un
	 * dry-run casserait dès qu'une étape lit l'`uuid` renvoyé par la précédente.
	 */
	dryRunResult?: unknown
	/** 404 toléré → `undefined` au lieu d'une exception (sondes d'existence). */
	allow404?: boolean
	/**
	 * Reçoit les en-têtes de la réponse réelle. Seul `x-oauth-scopes` en dépend
	 * — les portées d'un PAT classique ne se lisent nulle part ailleurs.
	 */
	captureHeaders?: (headers: Headers) => void
}

export interface RequesterOptions {
	dryRun?: boolean
	/** Notifié pour CHAQUE appel, émis ou non. Sert au journal et au dry-run. */
	onCall?: (call: HttpCall, dryRun: boolean) => void
	/** Injection pour les tests ; `globalThis.fetch` par défaut. */
	fetchImpl?: typeof fetch
}

export class HttpError extends Error {
	constructor(
		readonly status: number,
		readonly url: string,
		readonly bodyText: string,
	) {
		super(`HTTP ${status} sur ${url}\n${bodyText}`)
		this.name = "HttpError"
	}
}

/**
 * Exécute (ou simule) les appels HTTP JSON.
 *
 * En dry-run, AUCUNE requête n'est émise — pas même les GET. C'est délibéré :
 * un dry-run doit être exécutable sans token valide, donc il ne peut rien
 * valider côté serveur. C'est le prix d'une simulation qui ne touche rien.
 */
export class Requester {
	private readonly dryRun: boolean
	private readonly onCall?: (call: HttpCall, dryRun: boolean) => void
	private readonly fetchImpl: typeof fetch

	constructor(opts: RequesterOptions = {}) {
		this.dryRun = opts.dryRun ?? false
		this.onCall = opts.onCall
		this.fetchImpl = opts.fetchImpl ?? globalThis.fetch
	}

	get isDryRun(): boolean {
		return this.dryRun
	}

	async request<T>(opts: RequestOptions): Promise<T> {
		const {
			method,
			url,
			body,
			label,
			headers,
			dryRunResult,
			allow404,
			captureHeaders,
		} = opts
		this.onCall?.({ method, url, body, label }, this.dryRun)

		if (this.dryRun) return dryRunResult as T

		const response = await this.fetchImpl(url, {
			method,
			headers: {
				accept: "application/json",
				...(body === undefined ? {} : { "content-type": "application/json" }),
				...headers,
			},
			body: body === undefined ? undefined : JSON.stringify(body),
		})

		captureHeaders?.(response.headers)

		if (response.status === 404 && allow404) return undefined as T

		if (!response.ok) {
			throw new HttpError(response.status, url, await response.text())
		}

		// 204 et les corps vides sont légitimes (DELETE, certains PATCH).
		const text = await response.text()
		if (!text) return undefined as T
		try {
			return JSON.parse(text) as T
		} catch {
			return text as T
		}
	}
}
